/* Qualitative foot/contact animation. Timing and deformation are not measured results. */
(() => {
  'use strict';
  const pxAsset = key => window.PX_CONTENT_ASSETS?.[key] || '';
  const pxProduct = key => window.PX_SITE_CONFIG?.productUrls?.[key] || '';

  const models = {
    bouncy: {
      "name": "Bouncy",
      "color": "#a4cf76",
      "duration": 8500,
      "title": "Cushion the landing.<br>Feel the spring.",
      "copy": "Bouncy gives under the impact of your step, then springs back as you lift off. That combination puts cushioning and a lively response beneath your run.",
      "benefits": [
        [
          "Cushion each landing.",
          "The material compresses under impact, cushioning contact beneath your heel and forefoot."
        ],
        [
          "Feel the return.",
          "A more pronounced rebound brings a springy sensation to your stride. Choose it when you enjoy a responsive feel underfoot."
        ],
        [
          "Keep the cushioning coming.",
          "The open-cell cushioning recovers between contacts and resists lasting compression, helping preserve its feel through repeated wear."
        ]
      ],
      "scene": "Morning runs, training sessions and brisk walks—when you want cushioned contact with a noticeable spring.",
      "phases": [
        "Cushion impact",
        "Feel the spring",
        "Cushion again"
      ],
      "captions": [
        "Compression cushions the contact beneath your landing foot.",
        "Rebound adds the lively feeling you notice as you lift off.",
        "Recovery and resistance to packing down help keep cushioning in each step."
      ],
      "annotations": [
        [
          "Cushion the impact.",
          "Your foot lands. The material gives beneath the contact."
        ],
        [
          "A springy return.",
          "Pressure lifts. The cushion rebounds with a lively response."
        ],
        [
          "Ready to cushion again.",
          "The material recovers its shape and resists lasting compression."
        ]
      ],
      "callout": [
        "Impact cushioning",
        "Noticeable rebound",
        "Resists packing down"
      ]
    },
    embrace: {
      name: 'Embrace', color: '#9dcbdc', depth: 8, duration: 10500,
      title: 'Cushioning that<br>keeps coming back.',
      copy: 'A soft landing is only the start. Embrace’s cushioning is made to recover between steps and resist packing down with repeated use.',
      benefit: 'Softly contouring comfort as your day moves.',
      scene: 'Running errands. Working on your feet. Walking home.',
      benefits: [['Softer contact on hard floors.','A yielding cushion softens contact beneath your heel and forefoot as you stand and walk.'],['Resists packing down.','The open-cell cushioning resists lasting compression, helping preserve its soft feel through repeated wear.'],['Support beneath the softness.','A shaped base meets your arch, with soft cushioning between your foot and the firmer support.']],
      // Supply a portrait only after it has been matched to the reviewer; review attachments are not automatically avatars.
      review: {quote:'I feel less pain and tiredness after long distance walk with this shoe insoles.', author:'Elvis', date:'May 2023', context:'Embrace customer review · Earlier version · Excerpt', portrait:null},
      phases: ['Soft contact', 'Softly conforms', 'Cushions again'],
      captions: ['A softer place for your heel and forefoot to land.', 'Softly conforming contact, with a low-bounce feel.', 'Built to resist lasting compression, so cushioning keeps coming back.'],
      annotations: [
        ['Soft contact.', 'The cushion yields as your foot presses down.'],
        ['Contouring comfort.', 'It conforms beneath the contact of your foot.'],
        ['Cushioning that comes back.', 'Pressure lifts. The cushion recovers. Ready for the next step.']
      ]
    },
    plush: {
      "name": "Plush",
      "color": "#d5d5cd",
      "duration": 9500,
      "title": "Soft from the first step.<br>Spring in the next.",
      "copy": "Plush yields easily when your foot meets it, then springs back as you move. Soft contact, an elastic response and cushioning made to keep coming back.",
      "benefits": [
        [
          "Soften the first contact.",
          "An easily yielding cushion gives your heel and forefoot a softer surface inside your everyday shoes."
        ],
        [
          "Put spring into the softness.",
          "Elastic recovery adds a springy feeling as you walk. Choose Plush when you want softness with more bounce than Embrace."
        ],
        [
          "Softness beyond the first wear.",
          "The open-cell cushioning resists packing down, helping preserve the soft contact you chose it for through repeated use."
        ]
      ],
      "scene": "Commuting, errands and days out on foot—when a soft first step and a little spring are what make shoes feel good to you.",
      "phases": [
        "Soft contact",
        "Spring back",
        "Cushion again"
      ],
      "captions": [
        "An easily yielding surface softens contact beneath your foot.",
        "Elastic recovery brings a springy feel to the next step.",
        "Resistance to lasting compression helps preserve the softness underfoot."
      ],
      "annotations": [
        [
          "Soft, yielding contact.",
          "The cushion gives easily as your heel and forefoot settle onto it."
        ],
        [
          "Softness with spring.",
          "As pressure lifts, the material springs back beneath your foot."
        ],
        [
          "Softness that comes back.",
          "The cushion recovers and resists packing down with repeated use."
        ]
      ],
      "callout": [
        "Easily yielding",
        "Soft + springy",
        "Resists packing down"
      ]
    },
    fusion: {
      "name": "Fusion",
      "color": "#a2b6b8",
      "duration": 11000,
      "title": "Cushioned contact.<br>Less sink underfoot.",
      "copy": "Fusion combines two cushioning technologies for a firmer, more substantial feel than Embrace. There is give beneath your weight, with less easy sinking and a low-bounce response.",
      "benefits": [
        [
          "Comfort with more substance.",
          "A conforming surface and a more resistant response work together, cushioning contact without the deep softness of Plush."
        ],
        [
          "Settle onto a firmer feel.",
          "Less easy compression than Embrace and low rebound suit people who prefer a more settled sensation while standing and moving between tasks."
        ],
        [
          "Recovery without the bounce.",
          "Low rebound still allows the material to recover. Its open-cell cushioning resists lasting compression, helping maintain cushioning through repeated wear."
        ]
      ],
      "scene": "At the counter, at your workbench and across the floor—when very soft insoles feel too sinky and you prefer a firmer cushion.",
      "phases": [
        "Two materials",
        "Firmer feel",
        "Cushion again"
      ],
      "captions": [
        "Two cushioning technologies bring conforming contact and substance together.",
        "Less easy compression gives you a more substantial, low-bounce feel.",
        "The material recovers and resists packing down, without a pronounced spring."
      ],
      "annotations": [
        [
          "Two technologies. One cushion.",
          "A conforming surface works with a more resistant response beneath."
        ],
        [
          "Give, with less sink.",
          "The cushion takes your weight with a firmer feel than Embrace."
        ],
        [
          "Cushioning that comes back.",
          "Low bounce. Shape recovery. Resistance to lasting compression."
        ]
      ],
      "callout": [
        "Composite cushioning",
        "Substantial · Low bounce",
        "Resists packing down"
      ]
    },
    honeycomb: {
      "name": "HoneyComb",
      "color": "#e7c356",
      "duration": 11500,
      "title": "Give for movement.<br>Resistance for impact.",
      "copy": "HoneyComb responds to how quickly pressure arrives. It yields under gradual pressure and resists compression more firmly under rapid impact, bringing two responses to changing demands underfoot.",
      "benefits": [
        [
          "Give for everyday movement.",
          "Under gradual pressure, the cushion stays flexible and yields with the contact beneath your foot."
        ],
        [
          "More resistance when impact arrives.",
          "Under a rapid impact, the material becomes firmer and harder to compress. Choose it for this impact-responsive feel, rather than pronounced bounce."
        ],
        [
          "Ready for repeated contact.",
          "The open-cell material recovers after pressure and resists packing down, helping preserve cushioning as you keep moving."
        ]
      ],
      "scene": "Walking, taking stairs and picking up the pace—when you want flexible contact with a firmer response to quicker impacts.",
      "phases": [
        "Slow pressure",
        "Fast impact",
        "Cushion again"
      ],
      "captions": [
        "Gradual pressure meets a flexible, yielding cushion.",
        "A quicker impact meets greater resistance to compression.",
        "The material recovers and resists lasting compression for repeated contact."
      ],
      "annotations": [
        [
          "Slow pressure. Flexible feel.",
          "As pressure builds gradually, the material yields beneath your foot."
        ],
        [
          "Quick impact. Firmer response.",
          "When impact arrives rapidly, the material resists compression more firmly."
        ],
        [
          "Ready to cushion again.",
          "Pressure lifts. The material recovers and resists packing down."
        ]
      ],
      "callout": [
        "Gradual load · Flexible",
        "Rapid load · More resistant",
        "Resists packing down"
      ]
    }
  };

  const profiles = {
    bouncy: {depth:28, stops:[[0,'#cfecaa'],[.45,'#a4ce76'],[1,'#608b3e']], ink:'#39552d', highlight:'#e7f4d4', callouts:['Cushioned contact','Springy return',''], positions:[.18,.33,.89]},
    embrace: {depth:32, stops:[[0,'#b9dcea'],[.45,'#8dbdd3'],[1,'#548da9']], ink:'#365d72', highlight:'#d9ecf4', callouts:['','Low-bounce feel',''], positions:[.16,.48,.89]},
    plush: {depth:42, stops:[[0,'#eeeee8'],[.45,'#c4c7c1'],[1,'#838b83']], ink:'#46534a', highlight:'#f5f5ef', callouts:['Easy give','Soft + springy',''], positions:[.20,.37,.89]},
    fusion: {depth:23, stops:[[0,'#8eb8c3'],[.42,'#abc9d0'],[.59,'#e1e7e3'],[1,'#aabbb5']], ink:'#36595d', highlight:'#e5efef', callouts:['Composite cushion','Less sink',''], positions:[.16,.48,.89]},
    honeycomb: {depth:34, stops:[[0,'#f8e4a0'],[.45,'#e9c85e'],[1,'#ad862c']], ink:'#72571e', highlight:'#fff0bb', callouts:['Flexible feel','Firmer on impact',''], positions:[.23,.55,.89]}
  };

  const bindings = new WeakMap();
  let sequence = 0;
  const sprite = pxAsset('shoe-cutaway-components-v715.png');

  function smooth(value) { const x = Math.max(0, Math.min(1, value)); return x*x*(3-2*x); }
  function curve(points, move = true) {
    let d = move ? `M${points[0][0]},${points[0][1]}` : '';
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[Math.max(0, i - 1)], b = points[i], c = points[i + 1], z = points[Math.min(points.length - 1, i + 2)];
      d += `C${b[0]+(c[0]-a[0])/6},${b[1]+(c[1]-a[1])/6} ${c[0]-(z[0]-b[0])/6},${c[1]-(z[1]-b[1])/6} ${c[0]},${c[1]}`;
    }
    return d;
  }
  // The shared sample geometry follows the approved Embrace V7.17 study.
  // Timing, indentation and color divisions illustrate feel; they are not measured performance comparisons.
  const sampleSole = [[150,1110],[180,1136],[220,1146],[280,1149],[350,1143],[420,1129],[480,1115],[540,1109],[600,1114],[680,1130],[760,1147],[840,1157],[920,1159],[1000,1156],[1080,1154],[1135,1148],[1160,1133]];
  function sampleSurface(compression) {
    return [[76,476],[140,476], ...sampleSole.map(([x,y])=>[148+(x-130)*.58, Math.max(476,476+(y-1159)*.58+compression)]), [788,476],[836,476]];
  }
  // 68.2 units = 55% of the previous 124-unit sample thickness.
  function sampleBlock(points) { return `${curve(points)}V535.2Q836,544.2 827,544.2H85Q76,544.2 76,535.2Z`; }
  function renderMaterial(id,m,phaseButtons,key) {
    const top=sampleSurface(0), outline=sampleBlock(top), profile=profiles[key];
    return `<div class="px-underfoot px-underfoot--material-study" id="${id}" data-underfoot-model="${key}" style="--underfoot-color:${m.color}">
      <div class="px-underfoot__visual">
        <div class="px-underfoot__top"><span>${m.name} · The material in motion</span><button type="button" data-underfoot-toggle aria-label="Pause foot animation">Pause</button></div>
        <svg id="${id}-diagram" class="px-underfoot__diagram" viewBox="0 0 900 584" role="img" aria-labelledby="${id}-title ${id}-desc">
          <title id="${id}-title">${m.name}: ${m.phases.join(", ").toLowerCase()}</title>
          <desc id="${id}-desc">A socked foot repeatedly presses into one rectangular material sample. ${m.annotations.map(entry=>entry[1]).join(' ')} Material response illustrated; thickness and movement are exaggerated for clarity.</desc>
          <defs>
            <clipPath id="${id}-foot-row"><rect x="115" y="674" width="1080" height="486"/></clipPath>
            <clipPath id="${id}-block-clip"><path data-underfoot-block-texture d="${outline}"/></clipPath>
            <linearGradient id="${id}-material" x1="0" y1="0" x2="0" y2="1">${profile.stops.map(([offset,color])=>`<stop offset="${offset}" stop-color="${color}"/>`).join('')}</linearGradient>
            <pattern id="${id}-pores" width="19" height="17" patternUnits="userSpaceOnUse"><circle cx="3" cy="5" r=".8" fill="#315c70" opacity=".17"/><circle cx="12" cy="13" r=".65" fill="#fff" opacity=".36"/><circle cx="16" cy="3" r=".6" fill="#315c70" opacity=".15"/></pattern>
            <radialGradient id="${id}-shadow"><stop stop-color="#243c49" stop-opacity=".18"/><stop offset="1" stop-color="#243c49" stop-opacity="0"/></radialGradient>
          </defs>
          <text class="px-underfoot__motion-label" x="46" y="61" data-underfoot-label>${m.annotations[0][0]}</text>
          <foreignObject x="46" y="79" width="808" height="74"><p xmlns="http://www.w3.org/1999/xhtml" class="px-underfoot__motion-sublabel" data-underfoot-sublabel>${m.annotations[0][1]}</p></foreignObject>
          <ellipse cx="456" cy="551" rx="407" ry="22" fill="url(#${id}-shadow)"/>
          <path data-underfoot-cushion d="${outline}" fill="url(#${id}-material)" stroke="${profile.ink}" stroke-width="1.2"/>
          <g clip-path="url(#${id}-block-clip)"><rect x="76" y="472" width="760" height="76" fill="url(#${id}-pores)"/></g>
          <path data-underfoot-block-top d="${curve(top)}" fill="none" stroke="${profile.highlight}" stroke-width="5"/>
          <path data-underfoot-contact-line d="${curve(top)}" fill="none" stroke="${profile.ink}" stroke-width="2.5" opacity="0"/>
          <g data-underfoot-foot opacity="1"><g transform="translate(148 476) scale(.58) translate(-130 -1159)"><image href="${sprite}" width="1254" height="1254" clip-path="url(#${id}-foot-row)"/></g></g>
          <g data-underfoot-load opacity="0" fill="none" stroke="${profile.ink}" stroke-width="2.5"><path d="M300 348V391M292 382L300 391 308 382M641 385V428M633 419L641 428 649 419"/></g>
          <g data-underfoot-callout opacity="0"><path d="M642 220H781V463" fill="none" stroke="${key==='embrace'?'#527387':profile.ink}" stroke-width="1.5"/><text x="638" y="207" text-anchor="end" class="px-underfoot__motion-callout" data-underfoot-callout-text>${profile.callouts[0]}</text><circle cx="781" cy="468" r="4" fill="${key==='embrace'?'#527387':profile.ink}"/></g>
        </svg>
        <div class="px-underfoot__phases" role="group" aria-label="Explore ${m.name}’s material response">${phaseButtons}</div>
        <p class="px-underfoot__caption" data-underfoot-caption>${m.captions[0]}</p>
        <p class="px-underfoot__note">Material response illustrated · Thickness enlarged for clarity.</p>
      </div>
      <div class="px-underfoot__story"><h3>${m.title}</h3><p>${m.copy}</p><div class="px-underfoot__benefits">${m.benefits.map(([heading,body])=>`<div><h4>${heading}</h4><p>${body}</p></div>`).join('')}</div><div class="px-underfoot__scene"><span>In your day</span><p>${m.scene}</p></div></div>
      ${m.review?`<figure class="px-underfoot__review"><span>A customer’s experience</span><blockquote>“${m.review.quote}”</blockquote><figcaption class="px-underfoot__review-person">${m.review.portrait?`<img class="px-underfoot__review-avatar" src="${m.review.portrait}" alt="${m.review.author}" width="56" height="56" loading="lazy">`:'<span class="px-underfoot__review-avatar px-underfoot__review-initial" aria-hidden="true">E</span>'}<div><strong>${m.review.author}</strong><span>${m.review.date}</span><span>${m.review.context}</span></div></figcaption></figure>`:''}
    </div>`;
  }

  function render({model}={}) {
    const key = String(model||'').toLowerCase(), m = models[key];
    if (!m) return '';
    const id = `px-underfoot-${++sequence}`;
    const phaseButtons = m.phases.map((label,i)=>`<button type="button" data-underfoot-phase="${i}" aria-pressed="${i===0}" aria-controls="${id}-diagram">${label}</button>`).join('');
    return renderMaterial(id,m,phaseButtons,key);
  }

  function elementsIn(root) {
    return [...(root.matches?.('.px-underfoot')?[root]:[]), ...root.querySelectorAll('.px-underfoot')];
  }

  function bind(component) {
    const m = models[component.dataset.underfootModel];
    if (!m) return null;
    const key = component.dataset.underfootModel;
    const visual = component.querySelector('[data-underfoot-foot]');
    const cushion = component.querySelector('[data-underfoot-cushion]');
    const contactLine = component.querySelector('[data-underfoot-contact-line]');
    const arrows = component.querySelector('[data-underfoot-load]');
    const toggle = component.querySelector('[data-underfoot-toggle]');
    const caption = component.querySelector('[data-underfoot-caption]');
    const buttons = [...component.querySelectorAll('[data-underfoot-phase]')];
    const label = component.querySelector('[data-underfoot-label]');
    const sublabel = component.querySelector('[data-underfoot-sublabel]');
    const blockTexture = component.querySelector('[data-underfoot-block-texture]');
    const blockTop = component.querySelector('[data-underfoot-block-top]');
    const callout = component.querySelector('[data-underfoot-callout]');
    const calloutText = component.querySelector('[data-underfoot-callout-text]');
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let desired = !media?.matches, visible = !('IntersectionObserver' in window), disposed = false;
    let frame = null, previous = null, elapsed = 0, generation = 0, activePhase = -1;
    let observer, resumeAfterDialog = false, suspended = false;

    function draw(progress) {
      const profile=profiles[key];
      let phase,load,lift,compression;
      if(key==='honeycomb') {
        phase=progress<.44?0:progress<.8?1:2;
        if(phase===0) {
          load=smooth((progress-.10)/.13)*(1-smooth((progress-.30)/.12));
          lift=progress<.10?(1-smooth(progress/.10))*24:smooth((progress-.32)/.10)*24;
          compression=load*profile.depth;
        } else if(phase===1) {
          load=smooth((progress-.48)/.03)*(1-smooth((progress-.66)/.10));
          lift=progress<.48?(1-smooth((progress-.44)/.04))*24:smooth((progress-.68)/.10)*24;
          // Faster arrival is met by a shallower indentation: a qualitative rate response.
          compression=load*19;
        } else {load=0;lift=24;compression=0;}
      } else if(key==='bouncy'||key==='plush') {
        const soft=key==='plush', pressEnd=soft ? .25 : .18, releaseAt=soft ? .31 : .28, releaseSpan=soft ? .17 : .10;
        phase=progress<releaseAt?0:progress<.64?1:2;
        load=progress<releaseAt?smooth((progress-.09)/(pressEnd-.09)):1-smooth((progress-releaseAt)/releaseSpan);
        lift=progress<.09?(1-smooth(progress/.09))*24:progress>releaseAt+.02?smooth((progress-releaseAt-.02)/releaseSpan)*24:0;
        compression=load*profile.depth;
      } else {
        phase=progress<.27?0:progress<.64?1:2;
        load=progress<.27?smooth((progress-.1)/.17):progress<.64?1:1-smooth((progress-.64)/.16);
        lift=progress<.1?(1-smooth(progress/.1))*24:progress>.68?smooth((progress-.68)/.16)*24:0;
        compression=load*profile.depth;
      }
      const top=sampleSurface(compression), outline=sampleBlock(top);
      cushion.setAttribute('d',outline);
      blockTexture?.setAttribute('d',outline);
      blockTop?.setAttribute('d',curve(top));
      contactLine.setAttribute('d',curve(top));
      contactLine.setAttribute('opacity',String(load*.55));
      // The foot lifts itself; the material never propels it or overshoots its resting surface.
      visual.setAttribute('transform',`translate(0,${compression-lift})`);
      visual.setAttribute('opacity','1');
      arrows.setAttribute('opacity',String((phase===0||(key==='honeycomb'&&phase===1))?load*.6:0));
      callout?.setAttribute('opacity',String(profile.callouts[phase]?1:0));
      component.dataset.underfootPhase=String(phase);
      if(activePhase!==phase) {
        activePhase=phase;
        buttons.forEach((b,i)=>b.setAttribute('aria-pressed',String(i===phase)));
        caption.textContent=m.captions[phase];
        if(label) label.textContent=m.annotations[phase][0];
        if(sublabel) sublabel.textContent=m.annotations[phase][1];
        if(calloutText) calloutText.textContent=profile.callouts[phase];
      }
    }

    function updateButton() {
      toggle.textContent=desired?'Pause':'Play';
      toggle.setAttribute('aria-label',desired?'Pause foot animation':'Play foot animation');
      component.dataset.underfootPlaying=String(frame!==null);
    }
    function stop() {
      generation+=1;
      if(frame!==null) cancelAnimationFrame(frame);
      frame=null; previous=null; updateButton();
    }
    function reconcile() {
      if(disposed||suspended||!desired||!visible||document.hidden) { stop(); return; }
      if(frame!==null) return;
      const token=++generation;
      const tick=time=>{
        if(disposed||token!==generation||suspended||!desired||!visible||document.hidden) return;
        if(previous!==null) elapsed+=Math.min(64,Math.max(0,time-previous));
        previous=time;
        draw((elapsed%m.duration)/m.duration);
        frame=requestAnimationFrame(tick);
      };
      frame=requestAnimationFrame(tick); updateButton();
    }
    function pause() { desired=false; resumeAfterDialog=false; stop(); }
    function suspend() {
      if(!suspended) resumeAfterDialog=desired;
      suspended=true;stop();
    }
    function resume() {
      if(!suspended)return;
      suspended=false;desired=resumeAfterDialog&&!media?.matches;resumeAfterDialog=false;reconcile();
    }
    function onClick(event) {
      const button=event.target.closest('button');
      if(!button||!component.contains(button)) return;
      if(button.matches('[data-underfoot-toggle]')) { desired=!desired; reconcile(); }
      else if(button.matches('[data-underfoot-phase]')) {
        const phase=Number(button.dataset.underfootPhase);
        if(!Number.isInteger(phase)||phase<0||phase>2) return;
        const positions=profiles[key].positions;
        elapsed=positions[phase]*m.duration;draw(positions[phase]);
        reconcile();
      }
    }
    function onVisibility(){ reconcile(); }
    function onMotion(){ if(media.matches) pause(); }
    component.addEventListener('click',onClick);
    document.addEventListener('visibilitychange',onVisibility);
    media?.addEventListener?.('change',onMotion);
    if('IntersectionObserver' in window) {
      observer=new IntersectionObserver(entries=>{visible=entries.some(e=>e.isIntersecting&&e.intersectionRatio>=.1);reconcile();},{threshold:[0,.1]});
      observer.observe(component);
    }
    draw(0);reconcile();
    function destroy(){
      if(disposed)return;disposed=true;stop();observer?.disconnect();
      component.removeEventListener('click',onClick);document.removeEventListener('visibilitychange',onVisibility);
      media?.removeEventListener?.('change',onMotion);bindings.delete(component);
    }
    bindings.set(component,{pause,suspend,resume,destroy});return destroy;
  }
  function init(root=document) {
    const cleanups=elementsIn(root).filter(e=>!bindings.has(e)).map(bind).filter(Boolean);
    return ()=>cleanups.forEach(fn=>fn());
  }
  function pause(root=document) { elementsIn(root).forEach(e=>bindings.get(e)?.pause()); }
  function suspend(root=document) { elementsIn(root).forEach(e=>bindings.get(e)?.suspend()); }
  function resume(root=document) { elementsIn(root).forEach(e=>bindings.get(e)?.resume()); }
  window.PXUnderfootMotion={render,init,pause,suspend,resume};
})();
